/**
 * MKRY-5189: camera edge filtering settings carried by updateConfig.
 *
 * Kept as plain JS so the vendored browser client and the UI can share it
 * without a move-sdk release.
 *
 * Wire contract:
 *   detectionTypes      unique strings from
 *                        person | vehicle | animal | package | motion
 *                        ("motion" = unidentified motion detections)
 *   personConfidence    integer 0..100
 *   vehicleConfidence   integer 0..100
 *   animalConfidence    integer 0..100
 *   packageConfidence   integer 0..100
 * Motion has no confidence setting. Omitted settings remain unchanged.
 */

/** Object classes that take a confidence threshold. */
export const EDGE_FILTER_CLASSES = ['person', 'vehicle', 'animal', 'package'];

/** Everything selectable in detectionTypes. */
export const EDGE_FILTER_TYPES = [...EDGE_FILTER_CLASSES, 'motion'];

export const CONFIDENCE_KEYS = EDGE_FILTER_CLASSES.map(
  (type) => `${type}Confidence`,
);

export const EDGE_FILTER_KEYS = ['detectionTypes', ...CONFIDENCE_KEYS];

/**
 * Validate wire values without coercing booleans, strings, or fractional
 * scores. Throws an Error describing the first problem found.
 * @param {object} update
 */
export function validateEdgeFilterUpdate(update) {
  if (
    !update ||
    typeof update !== 'object' ||
    Array.isArray(update) ||
    !Object.keys(update).length
  ) {
    throw new Error('An edge filter update must contain at least one setting');
  }
  if (Object.keys(update).some((key) => !EDGE_FILTER_KEYS.includes(key))) {
    throw new Error('Unknown edge filter setting');
  }
  if ('detectionTypes' in update) {
    const selected = update.detectionTypes;
    // Materialize sparse slots as undefined so the type check rejects them.
    if (
      !Array.isArray(selected) ||
      Array.from(selected).some((type) => !EDGE_FILTER_TYPES.includes(type)) ||
      new Set(selected).size !== selected.length
    ) {
      throw new Error(
        'detectionTypes must contain unique person, vehicle, animal, package, or motion values',
      );
    }
  }
  CONFIDENCE_KEYS.forEach((key) => {
    if (!(key in update)) {
      return;
    }
    const value = update[key];
    if (!Number.isInteger(value) || value < 0 || value > 100) {
      throw new Error(`${key} must be an integer percentage from 0 to 100`);
    }
  });
}
